import { z } from "zod"
import { ChannelEnum } from "../../campaigns/commonTypes"
import { TimeFrameEnum, TimeFrameSchema } from "../../insights/timeFrame"
import { createFilterBySchema } from "../../shared/filter_dtos"
import { PaginationSchema } from "../../shared/pagination_dtos"
import { QuerySchema } from "../../shared/query_dtos"

export const BackofficeFlagsKindEnum = ["Friction", "Issue"] as const
export const BackofficeFlagsTreemapGroupByEnum = ["key", "company"] as const

/**
 * API-only namespace value for flags whose keys do not contain a namespace separator.
 * PostgreSQL text values cannot contain NUL, so this cannot collide with a stored namespace.
 * Clients should display a translated label instead of this value.
 */
export const BackofficeFlagsUnnamespacedNamespace = "\u0000"

const DecimalIdSchema = z.string().regex(/^[1-9]\d*$/)

const KeysFilterSchema = z.preprocess((value) => {
  if (value == null || value === "") return undefined
  return Array.isArray(value) ? value : [value]
}, z.array(z.string()).optional())

export const BackofficeFlagsFilterBySchema = createFilterBySchema([
  { key: "companyId", value: DecimalIdSchema.optional() },
  {
    key: "includeLegacy",
    value: z
      .union([z.boolean(), z.string()])
      .transform((value) => value === true || value === "true")
      .optional(),
  },
  { key: "campaignId", value: DecimalIdSchema.optional() },
  { key: "channel", value: z.enum(ChannelEnum).optional() },
  { key: "kind", value: z.enum(BackofficeFlagsKindEnum).optional() },
  { key: "namespace", value: z.string().optional() },
  { key: "key", value: KeysFilterSchema },
])

const backofficeFlagsChartQueryShape = QuerySchema.extend(
  BackofficeFlagsFilterBySchema.shape,
).extend({
  timeFrame: TimeFrameSchema.optional().default(TimeFrameEnum.Last3Days),
}).shape

export const GetBackofficeFlagsActivitySchema = z.object({
  ...backofficeFlagsChartQueryShape,
})

export const GetBackofficeFlagsTimelineSchema = z.object({
  ...BackofficeFlagsFilterBySchema.shape,
})

export const GetBackofficeFlagsCampaignsSchema = z.object({
  ...QuerySchema.shape,
})

export const GetBackofficeFlagsTreemapSchema = z.object({
  ...backofficeFlagsChartQueryShape,
  groupBy: z.enum(BackofficeFlagsTreemapGroupByEnum).default("key"),
})

export const GetBackofficeFlagsConversationsSchema = z.object({
  ...PaginationSchema.extend(QuerySchema.shape)
    .extend(BackofficeFlagsFilterBySchema.shape)
    .extend({
      timeFrame: TimeFrameSchema.optional().default(TimeFrameEnum.Last3Days),
    }).shape,
})
